import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService, Notification } from '../../services/notification.service';
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
    selector: 'app-notification',
    templateUrl: './notification.component.html',
    styleUrls: ['./notification.component.scss'],
    standalone: false,
    animations: [
        trigger('slideIn', [
            state('void', style({
                transform: 'translateY(-100%)',
                opacity: 0
            })),
            state('*', style({
                transform: 'translateY(0)',
                opacity: 1
            })),
            transition('void => *', animate('300ms ease-out')),
            transition('* => void', animate('300ms ease-in'))
        ])
    ]
})
export class NotificationComponent implements OnInit, OnDestroy {
    public notification: Notification | null = null;
    private destroy$ = new Subject<void>();
    private autoHideTimeout: any;
    public isValidationError = false;

    constructor(private notificationService: NotificationService) {}

    ngOnInit(): void {
        this.notificationService.notification$
            .pipe(takeUntil(this.destroy$))
            .subscribe(notification => {
                this.notification = notification;
                this.isValidationError = notification?.isValidationError ?? false;
                
                // If duration is explicitly set, use it (0 = never dismiss)
                if (notification && notification.duration !== undefined) {
                    this.clearAutoHideTimeout();
                    if (notification.duration > 0) {
                        this.autoHideTimeout = setTimeout(() => {
                            this.notificationService.clear();
                        }, notification.duration);
                    }
                    // If duration is 0, don't set auto-hide timeout (notification stays until user closes)
                } else {
                    // Default behavior: auto-hide after type-specific delay
                    if (notification && (notification.type === 'success' || notification.type === 'info' || notification.type === 'warning')) {
                        this.clearAutoHideTimeout();
                        this.autoHideTimeout = setTimeout(() => {
                            this.notificationService.clear();
                        }, 5000);
                    } else if (notification && notification.type === 'error') {
                        // Keep errors visible longer (10 seconds)
                        this.clearAutoHideTimeout();
                        this.autoHideTimeout = setTimeout(() => {
                            this.notificationService.clear();
                        }, 10000);
                    }
                }
            });
    }

    ngOnDestroy(): void {
        this.clearAutoHideTimeout();
        this.destroy$.next();
        this.destroy$.complete();
    }

    public close(): void {
        this.clearAutoHideTimeout();
        this.notificationService.clear();
    }

    private clearAutoHideTimeout(): void {
        if (this.autoHideTimeout) {
            clearTimeout(this.autoHideTimeout);
            this.autoHideTimeout = null;
        }
    }

    public getIcon(): string {
        if (!this.notification) return 'info';
        
        switch (this.notification.type) {
            case 'success':
                return 'check_circle';
            case 'error':
                return 'error';
            case 'warning':
                return 'warning';
            case 'info':
            default:
                return 'info';
        }
    }
}
